import {
  Add,
  Delete,
  Download,
  Edit,
  Upload,
  Visibility,
} from '@mui/icons-material';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { getFormTeplateTypeForExport, importFormTemplateType } from 'api';
import { FormTemplate, FormTemplateType } from 'api/models';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import useHasAccess from 'hooks/useHasAccess';
import { query, remove } from 'operations';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';
import {
  ToastSeverity,
  dialogState,
  toastStateSelector,
  formTemplateTypeListState,
  confirmDialogState,
  sampleTemplateTypeListState,
} from 'state';
import { Permission } from 'state/permissionState';
import { DialogTypes, GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';

export function getLatestTemplate(templates?: FormTemplate[]) {
  return templates?.reduce((prev, current) =>
    prev.version > current.version ? prev : current,
  );
}

export const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface FormTemplateTypesTableProps {
  mode: FormTemplateEditorMode;
  templates: FormTemplateType[];
  loading: boolean;
}

export default function FormTemplateTypesTable({
  mode,
  templates,
  loading,
}: FormTemplateTypesTableProps) {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const setConfirmDialog = useSetRecoilState(confirmDialogState);
  const openDialog = useSetRecoilState(dialogState);
  const setFormTemplateTypes = useSetRecoilState(formTemplateTypeListState);
  const setSampleTemplateTypes = useSetRecoilState(sampleTemplateTypeListState);

  const setToast = useSetRecoilState(toastStateSelector);
  const navigate = useNavigate();

  const isForm = mode === FormTemplateEditorMode.default;

  const canEditAndDeleteFormTemplateTypes = useHasAccess([
    Permission.WRITE_FORM_TEMPLATE,
  ]);

  const onConfirmDelete = (templateId: string) => {
    return async () => {
      try {
        await remove({
          type: ModelTypes.formTemplateType,
          ids: {
            id: templateId,
            projectId: projectId,
          },
        });

        const { data } = await query<FormTemplateType[]>({
          type: ModelTypes.formTemplateType,
          queryParams: {
            type: mode,
          },
          ids: {
            projectId: projectId,
          },
        });

        if (mode === FormTemplateEditorMode.default) {
          setFormTemplateTypes(data);
        } else {
          setSampleTemplateTypes(data);
        }

        setConfirmDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('projectCreation.errors.deletionFailed'),
          severity: ToastSeverity.error,
        });
      }
    };
  };

  const onFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const formTemplateType = JSON.parse(reader.result as string);
        await importFormTemplateType({
          projectId,
          data: formTemplateType,
        });

        const { data: formTemplateTypes } = await query<FormTemplateType[]>({
          type: ModelTypes.formTemplateType,
          ids: {
            projectId: projectId,
          },
        });

        setFormTemplateTypes(formTemplateTypes);

        setToast({
          isOpen: true,
          message: t('general.success.import'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        setToast({
          isOpen: true,
          message: e.response?.data?.error?.[0].message,
          severity: ToastSeverity.error,
        });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <>
      <Box display="flex" flexDirection="row" justifyContent={'space-between'}>
        <Typography variant="h5" ml={1} mt={2}>
          {mode === FormTemplateEditorMode.default
            ? t('forms.formTemplateTypes')
            : t('forms.sampleTemplateTypes')}
        </Typography>
        <Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            sx={{ mr: 1, mt: 2 }}
            disabled={!canEditAndDeleteFormTemplateTypes}
            onClick={() =>
              openDialog({
                isOpen: true,
                dialogType: DialogTypes.addFormTemplateType,
                data: {
                  mode,
                },
              })
            }
          >
            {isForm
              ? t('forms.addFormTemplateType')
              : t('forms.addSampleTemplateType')}
          </Button>

          <IconButton
            size="small"
            sx={{ mt: 2, mr: 1, height: 30 }}
            component="label"
            role={undefined}
            tabIndex={-1}
            title="Import"
          >
            <Upload fontSize="small" />
            <VisuallyHiddenInput onChange={onFileUpload} type="file" />
          </IconButton>
        </Box>
      </Box>
      <TableContainer sx={{ mt: 1, pb: 1 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                {t('general.name')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                {t('general.version')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                {t('general.operations')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ position: 'relative' }}>
            {loading ? (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
              />
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography align="center">{t('general.noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map(formTemplateType => {
                const latestTemplate = getLatestTemplate(
                  formTemplateType.formTemplates,
                );
                return (
                  <TableRow
                    key={`form-template-types-table-${formTemplateType.id}`}
                  >
                    <TableCell align="left">{formTemplateType.name}</TableCell>
                    <TableCell align="left">
                      <Chip
                        color={
                          latestTemplate?.status ===
                          FormTemplateStatus.Published
                            ? 'success'
                            : 'warning'
                        }
                        label={
                          latestTemplate?.version
                            ? `${latestTemplate?.version ?? ''}. ${t(
                                'general.version',
                              )}`
                            : t('forms.formTemplateStatus.draft')
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          let path = getRoutePath(
                            mode === FormTemplateEditorMode.sample
                              ? RoutePaths.sampleTemplateEditor
                              : RoutePaths.formTemplateEditor,
                            {
                              formTemplateTypeId: formTemplateType.id,
                              projectId,
                            },
                          );
                          if (!canEditAndDeleteFormTemplateTypes) {
                            path = getRoutePath(
                              mode === FormTemplateEditorMode.sample
                                ? RoutePaths.sampleTemplateEditorView
                                : RoutePaths.formTemplateEditorView,
                              {
                                formTemplateTypeId: formTemplateType.id,
                                projectId,
                              },
                            );
                          }

                          navigate(path);
                        }}
                      >
                        {canEditAndDeleteFormTemplateTypes ? (
                          <Tooltip title={t('general.edit')}>
                            <Edit fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('general.view')}>
                            <Visibility fontSize="small" />
                          </Tooltip>
                        )}
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={!canEditAndDeleteFormTemplateTypes}
                        onClick={async () => {
                          const formTempalateTypeResponse =
                            await getFormTeplateTypeForExport({
                              projectId,
                              formTemplateTypeId: formTemplateType.id,
                            });

                          const blob = new Blob(
                            [JSON.stringify(formTempalateTypeResponse)],
                            {
                              type: 'application/json',
                            },
                          );
                          const href = await URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = href;
                          link.download = `${formTempalateTypeResponse.formTemplateType.attributes.name}.json`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        <Tooltip title={t('general.export')}>
                          <Download fontSize="small" />
                        </Tooltip>
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={!canEditAndDeleteFormTemplateTypes}
                        onClick={() => {
                          setConfirmDialog({
                            isOpen: true,
                            dialogType: DialogTypes.confirmDelete,
                            action: onConfirmDelete(formTemplateType.id),
                          });
                        }}
                      >
                        <Tooltip title={t('general.delete')}>
                          <Delete fontSize="small" />
                        </Tooltip>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
